import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Filter } from 'lucide-react';
import { MusicIcon, VisualArtIcon, WritingIcon, VideoIcon } from '../../icons';
import { ProjectDto } from '../../../services';
import './PieceKanban.css';

interface PieceKanbanProps {
  pieces: ProjectDto[];
  isLoading?: boolean;
  onPieceClick?: (piece: ProjectDto) => void;
  onMovePiece?: (pieceId: string, newStage: string) => void;
  onAddPiece?: (stage?: string) => void;
}

interface KanbanColumn {
  stageName: string;
  pieces: ProjectDto[];
}

const disciplineIcons: Record<string, React.ReactNode> = {
  Music: <MusicIcon size={14} />,
  Art: <VisualArtIcon size={14} />,
  Writing: <WritingIcon size={14} />,
  Video: <VideoIcon size={14} />,
};

// Common stage categories that map different workflow stages
const commonStages = [
  { name: 'Planning', keywords: ['idea', 'concept', 'planning', 'outline', 'sketches'] },
  { name: 'In Progress', keywords: ['composition', 'arrangement', 'recording', 'creation', 'draft', 'first draft'] },
  { name: 'Review', keywords: ['mixing', 'refinement', 'revision', 'editing', 'review'] },
  { name: 'Complete', keywords: ['mastering', 'release', 'completion', 'share', 'publishing', 'complete'] },
];

const mapStageToCommonCategory = (stage: string): string => {
  const lowerStage = stage.toLowerCase();
  for (const common of commonStages) {
    if (common.keywords.some(keyword => lowerStage.includes(keyword))) {
      return common.name;
    }
  }
  return 'In Progress'; // Default
};

export const PieceKanban: React.FC<PieceKanbanProps> = ({
  pieces,
  isLoading = false,
  onPieceClick,
  onMovePiece,
  onAddPiece,
}) => {
  const { t } = useTranslation();
  const [draggedPiece, setDraggedPiece] = useState<ProjectDto | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [disciplineFilter, setDisciplineFilter] = useState<string>('all');

  // Get unique disciplines for filter
  const disciplines = useMemo(() => {
    const types = new Set(pieces.map(p => p.projectType));
    return Array.from(types);
  }, [pieces]);

  // Filter pieces by discipline
  const filteredPieces = useMemo(() => {
    if (disciplineFilter === 'all') return pieces;
    return pieces.filter(p => p.projectType === disciplineFilter);
  }, [pieces, disciplineFilter]);

  // Organize pieces into common stage columns
  const columns = useMemo((): KanbanColumn[] => {
    const columnMap = new Map<string, ProjectDto[]>();

    // Initialize columns
    commonStages.forEach(stage => {
      columnMap.set(stage.name, []);
    });

    // Sort pieces into columns
    filteredPieces.forEach(piece => {
      const category = mapStageToCommonCategory(piece.currentStage);
      const columnPieces = columnMap.get(category) || [];
      columnPieces.push(piece);
      columnMap.set(category, columnPieces);
    });

    return commonStages.map(stage => ({
      stageName: stage.name,
      pieces: columnMap.get(stage.name) || [],
    }));
  }, [filteredPieces]);

  const handleDragStart = useCallback((e: React.DragEvent, piece: ProjectDto) => {
    setDraggedPiece(piece);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', piece.id);

    const target = e.target as HTMLElement;
    setTimeout(() => target.classList.add('is-dragging'), 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const target = e.target as HTMLElement;
    target.classList.remove('is-dragging');
    setDraggedPiece(null);
    setDragOverColumn(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, column: KanbanColumn) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(column.stageName);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;
    if (!currentTarget.contains(relatedTarget)) {
      setDragOverColumn(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, column: KanbanColumn) => {
    e.preventDefault();
    const pieceId = e.dataTransfer.getData('text/plain');

    if (draggedPiece) {
      const currentCategory = mapStageToCommonCategory(draggedPiece.currentStage);
      if (currentCategory !== column.stageName) {
        // Map the common category back to a specific stage for the piece's discipline
        // For now, we'll use the column name as the new stage
        onMovePiece?.(pieceId, column.stageName);
      }
    }

    setDraggedPiece(null);
    setDragOverColumn(null);
  }, [draggedPiece, onMovePiece]);

  if (isLoading) {
    return (
      <div className="piece-kanban piece-kanban--loading">
        <div className="piece-kanban__skeleton">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="piece-kanban__skeleton-column">
              <div className="piece-kanban__skeleton-header" />
              <div className="piece-kanban__skeleton-cards">
                <div className="piece-kanban__skeleton-card" />
                <div className="piece-kanban__skeleton-card" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="piece-kanban">
      {/* Filter Controls */}
      <div className="piece-kanban__controls">
        <div className="piece-kanban__filter">
          <Filter size={16} />
          <select
            value={disciplineFilter}
            onChange={(e) => setDisciplineFilter(e.target.value)}
            className="piece-kanban__filter-select"
            aria-label={t('opusDashboard.filterByDiscipline', 'Filter by discipline')}
          >
            <option value="all">{t('opusDashboard.allDisciplines', 'All Disciplines')}</option>
            {disciplines.map(discipline => (
              <option key={discipline} value={discipline}>
                {discipline}
              </option>
            ))}
          </select>
        </div>
        <div className="piece-kanban__summary">
          <span className="piece-kanban__summary-count">{filteredPieces.length}</span>
          <span className="piece-kanban__summary-label">
            {t('opusDashboard.pieces', 'Pieces')}
          </span>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="piece-kanban__board">
        {columns.map((column) => (
          <div
            key={column.stageName}
            className={`piece-kanban__column ${dragOverColumn === column.stageName ? 'piece-kanban__column--drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, column)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column)}
          >
            <div className="piece-kanban__column-header">
              <h3 className="piece-kanban__column-title">{column.stageName}</h3>
              <span className="piece-kanban__column-count">{column.pieces.length}</span>
            </div>

            <div className="piece-kanban__column-content">
              {column.pieces.map((piece) => (
                <div
                  key={piece.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, piece)}
                  onDragEnd={handleDragEnd}
                  className={`piece-kanban__card-wrapper ${draggedPiece?.id === piece.id ? 'is-dragging' : ''}`}
                >
                  <button
                    className={`piece-kanban__card ${piece.status === 'Completed' ? 'piece-kanban__card--completed' : ''}`}
                    onClick={() => onPieceClick?.(piece)}
                  >
                    <div className="piece-kanban__card-header">
                      <span className="piece-kanban__card-discipline">
                        {disciplineIcons[piece.projectType] || <MusicIcon size={14} />}
                      </span>
                      <span className="piece-kanban__card-stage">{piece.currentStage}</span>
                    </div>
                    <h4 className="piece-kanban__card-title">{piece.title}</h4>
                    {piece.status === 'Completed' && (
                      <span className="piece-kanban__card-badge">
                        {t('opusDashboard.completed', 'Completed')}
                      </span>
                    )}
                  </button>
                </div>
              ))}

              {column.pieces.length === 0 && (
                <div className="piece-kanban__empty">
                  <p>{t('opusDashboard.noPiecesInStage', 'No pieces in this stage')}</p>
                </div>
              )}

              <button
                className="piece-kanban__add-btn"
                onClick={() => onAddPiece?.(column.stageName)}
                aria-label={t('opusDashboard.addPieceToStage', 'Add piece to {{stage}}', { stage: column.stageName })}
              >
                <Plus size={16} />
                {t('opusDashboard.addPiece', 'Add Piece')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PieceKanban;
